import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { products as productsApi, admin, categories as categoriesApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import { 
  ArrowLeft, Save, Upload, Trash2, GripVertical, 
  Image as ImageIcon, Plus, X, Loader2, Tag, Clock, Sparkles 
} from 'lucide-react';

const AdminProductEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const isNew = id === 'new';
  
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [categories, setCategories] = useState([]);
  
  const [product, setProduct] = useState({
    name: '',
    description: '',
    sku: '',
    category: '',
    price: '',
    sizing_type: 'unisex',
    color: '',
    images: [],
    custom_pricing: false,
    inventory: [],
    // Sale fields
    is_on_sale: false,
    sale_price: '',
    sale_type: '',
    sale_type_custom: '',
    // Stock Lot fields
    is_discontinued: false,
    is_stock_lot: false,
    stock_lot_price: ''
  });
  
  // Sale type options
  const saleTypeOptions = [
    { value: 'black_friday', label: 'Black Friday' },
    { value: 'boxing_day', label: 'Boxing Day' },
    { value: 'australia_day', label: 'Australia Day' },
    { value: 'christmas', label: 'Christmas Sale' },
    { value: 'new_year', label: 'New Year Sale' },
    { value: 'eofy', label: 'EOFY Sale' },
    { value: 'end_of_season', label: 'End of Season' },
    { value: 'clearance', label: 'Clearance' },
    { value: 'flash_sale', label: 'Flash Sale' },
    { value: 'custom', label: 'Custom...' },
  ];

  useEffect(() => {
    loadCategories();
    if (!isNew) {
      loadProduct();
    }
  }, [id]);

  const loadCategories = async () => {
    try {
      const response = await categoriesApi.getAll();
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to load categories');
    }
  };

  const loadProduct = async () => {
    setLoading(true);
    try {
      const response = await productsApi.getById(id);
      setProduct({
        ...response.data,
        price: response.data.price?.toString() || '',
        sale_price: response.data.sale_price?.toString() || '',
        stock_lot_price: response.data.stock_lot_price?.toString() || ''
      });
    } catch (error) {
      toast.error('Product not found');
      navigate('/admin/products');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setProduct(prev => ({ ...prev, [field]: value }));
  };

  const handleInventoryChange = (size, field, value) => {
    setProduct(prev => {
      const inventory = [...(prev.inventory || [])];
      const idx = inventory.findIndex(i => i.size === size);
      
      if (idx >= 0) {
        inventory[idx] = { ...inventory[idx], [field]: field === 'quantity' ? parseInt(value) || 0 : value };
      } else {
        inventory.push({ size, [field]: field === 'quantity' ? parseInt(value) || 0 : value });
      }
      
      return { ...prev, inventory };
    });
  };

  const getInventoryValue = (size, field) => {
    const inv = product.inventory?.find(i => i.size === size);
    return inv?.[field] || (field === 'quantity' ? 0 : '');
  };

  const handleSave = async () => {
    // Validation
    if (!product.name || !product.sku || !product.category || !product.price) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    // Sale validation
    if (product.is_on_sale && !product.sale_price) {
      toast.error('Please set a sale price');
      return;
    }
    
    if (product.is_on_sale && !product.sale_type) {
      toast.error('Please select a sale type');
      return;
    }
    
    if (product.is_on_sale && product.sale_type === 'custom' && !product.sale_type_custom) {
      toast.error('Please enter a custom sale name');
      return;
    }
    
    // Stock lot validation
    if (product.is_stock_lot && !product.is_discontinued) {
      toast.error('Product must be marked as discontinued to enable stock lot sale');
      return;
    }
    
    if (product.is_stock_lot && !product.stock_lot_price) {
      toast.error('Please set a stock lot price');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        ...product,
        price: parseFloat(product.price),
        sale_price: product.sale_price ? parseFloat(product.sale_price) : null,
        sale_type: product.is_on_sale ? product.sale_type : null,
        sale_type_custom: product.is_on_sale && product.sale_type === 'custom' ? product.sale_type_custom : null,
        stock_lot_price: product.stock_lot_price ? parseFloat(product.stock_lot_price) : null
      };
      
      if (isNew) {
        await admin.createProduct(payload);
        toast.success('Product created');
      } else {
        await admin.updateProduct(id, payload);
        toast.success('Product updated');
      }
      
      navigate('/admin/products');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (e) => {
    const files = e.target.files;
    console.log('Upload triggered, files:', files);
    if (!files || files.length === 0) {
      console.log('No files selected');
      return;
    }
    
    if (isNew) {
      toast.error('Please save the product first before uploading images');
      return;
    }
    
    console.log('Starting upload for product:', id);
    setUploading(true);
    try {
      for (const file of files) {
        console.log('Uploading file:', file.name, file.type, file.size);
        const response = await admin.uploadImage(id, file);
        console.log('Upload response:', response.data);
        
        // Update images in state
        if (response.data && response.data.images) {
          setProduct(prev => ({
            ...prev,
            images: response.data.images
          }));
        }
      }
      toast.success(`${files.length} image(s) uploaded successfully!`);
      
      // Reload product data to ensure images are displayed
      const productData = await products.getById(id);
      setProduct(productData.data);
      
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload image: ' + (error.message || 'Unknown error'));
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleImageDelete = async (imageUrl) => {
    if (isNew) return;
    
    try {
      const response = await admin.deleteImage(id, imageUrl);
      setProduct(prev => ({
        ...prev,
        images: response.data.images
      }));
      toast.success('Image deleted');
    } catch (error) {
      toast.error('Failed to delete image');
    }
  };

  const handleAddImageUrl = () => {
    const url = prompt('Enter image URL:');
    if (url) {
      setProduct(prev => ({
        ...prev,
        images: [...(prev.images || []), url]
      }));
    }
  };

  const getSizesForType = (sizingType) => {
    if (sizingType === 'onesize') return ['OS'];
    if (sizingType === 'ladyfit') return ['S', 'M', 'L', 'XL'];
    if (sizingType === 'kids') return ['1-2', '3-4', '5-6', '7-8', '9-10', '11-12'];
    if (sizingType === 'combined') return ['S/M', 'L/XL'];
    return ['S', 'M', 'L', 'XL', 'XXL'];
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 w-1/4 rounded" />
          <div className="h-[400px] bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  const sizes = getSizesForType(product.sizing_type);

  return (
    <div className="p-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" onClick={() => navigate('/admin/products')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <h1 className="text-2xl font-bold text-gray-900">
          {isNew ? 'Create Product' : 'Edit Product'}
        </h1>
      </div>

      <div className="space-y-8">
        {/* Basic Info */}
        <div className="border rounded-lg p-6">
          <h2 className="text-lg font-semibold mb-4">Product Information</h2>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label htmlFor="name">Product Name *</Label>
              <Input
                id="name"
                value={product.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="e.g., Classic Cotton Tee"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="sku">SKU / Style Code *</Label>
              <Input
                id="sku"
                value={product.sku}
                onChange={(e) => handleChange('sku', e.target.value)}
                placeholder="e.g., TSH-001"
                className="mt-1 font-mono"
              />
            </div>
            
            <div>
              <Label htmlFor="category">Category *</Label>
              <Select 
                value={product.category} 
                onValueChange={(v) => handleChange('category', v)}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.slug}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="price">Price (per unit) *</Label>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-gray-500">$</span>
                <Input
                  id="price"
                  type="number"
                  step="0.01"
                  min="0"
                  value={product.price}
                  onChange={(e) => handleChange('price', e.target.value)}
                  placeholder="0.00"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="sizing_type">Sizing Type</Label>
              <Select 
                value={product.sizing_type} 
                onValueChange={(v) => {
                  const allowedSizes = getSizesForType(v);
                  setProduct(prev => ({
                    ...prev,
                    sizing_type: v,
                    // Drop any inventory rows whose size isn't in the new sizing type
                    inventory: (prev.inventory || []).filter(i => allowedSizes.includes(i.size))
                  }));
                }}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unisex">Unisex (S-XXL)</SelectItem>
                  <SelectItem value="ladyfit">Lady-fit (S-XL)</SelectItem>
                  <SelectItem value="onesize">One Size</SelectItem>
                  <SelectItem value="kids">Kids (1-2 to 11-12)</SelectItem>
                  <SelectItem value="combined">Combined (S/M, L/XL)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="color">Color Options</Label>
              <Input
                id="color"
                value={product.color || ''}
                onChange={(e) => handleChange('color', e.target.value)}
                placeholder="e.g., Black, White, Navy"
                className="mt-1"
              />
            </div>
            
            <div className="flex items-center space-x-2 mt-6">
              <Checkbox
                id="custom_pricing"
                checked={product.custom_pricing}
                onCheckedChange={(v) => handleChange('custom_pricing', v)}
              />
              <Label htmlFor="custom_pricing" className="text-sm cursor-pointer">
                Custom pricing (exclude from bulk category updates)
              </Label>
            </div>
            
            <div className="col-span-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={product.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Product description..."
                rows={4}
                className="mt-1"
              />
            </div>
          </div>
        </div>

        {/* Images */}
        <div className="border rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Product Images</h2>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={handleAddImageUrl}
                type="button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add URL
              </Button>
              <Button 
                variant="outline" 
                onClick={() => fileInputRef.current?.click()}
                disabled={isNew || uploading}
                type="button"
              >
                {uploading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4 mr-2" />
                )}
                Upload File
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageUpload}
                className="hidden"
              />
            </div>
          </div>
          
          {isNew && (
            <div className="bg-yellow-50 border border-yellow-200 p-3 rounded text-sm text-yellow-800 mb-4">
              Save the product first to enable file uploads. You can add image URLs now.
            </div>
          )}

          {/* Quick URL Input */}
          <div className="mb-4 p-4 bg-[#F8FAFC] rounded-lg border border-[#E2E8F0]">
            <Label className="text-sm font-medium text-[#64748B] mb-2 block">Quick Add Image URL</Label>
            <div className="flex gap-2">
              <Input
                type="url"
                placeholder="https://example.com/image.jpg"
                id="image-url-input"
                className="flex-1"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    const url = e.target.value.trim();
                    if (url) {
                      setProduct(prev => ({
                        ...prev,
                        images: [...(prev.images || []), url]
                      }));
                      e.target.value = '';
                      toast.success('Image URL added');
                    }
                  }
                }}
              />
              <Button 
                type="button"
                variant="outline"
                onClick={() => {
                  const input = document.getElementById('image-url-input');
                  const url = input.value.trim();
                  if (url) {
                    setProduct(prev => ({
                      ...prev,
                      images: [...(prev.images || []), url]
                    }));
                    input.value = '';
                    toast.success('Image URL added');
                  } else {
                    toast.error('Please enter a valid URL');
                  }
                }}
              >
                Add
              </Button>
            </div>
            <p className="text-xs text-[#94A3B8] mt-1">Paste an image URL and press Enter or click Add</p>
          </div>
          
          {product.images?.length > 0 ? (
            <div className="grid grid-cols-4 gap-4">
              {product.images.map((img, idx) => (
                <div key={idx} className="relative group">
                  <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden border">
                    <img
                      src={img.startsWith('/uploads') ? `${process.env.REACT_APP_BACKEND_URL}/api${img}?t=${Date.now()}` : img}
                      alt={`Product ${idx + 1}`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23f1f5f9" width="100" height="100"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="%2394a3b8" font-size="12">No Image</text></svg>';
                      }}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => handleImageDelete(img)}
                    className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="w-4 h-4" />
                  </button>
                  {idx === 0 && (
                    <Badge className="absolute bottom-2 left-2 bg-[#002FA7]">Primary</Badge>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
              <ImageIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500">No images yet</p>
              <p className="text-sm text-gray-400 mt-1">Add image URLs above or upload files</p>
            </div>
          )}
        </div>

        {/* Sale Settings */}
        <div className="border rounded-lg p-6 border-red-200 bg-red-50/30">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Tag className="w-5 h-5 text-red-500" />
            Sale Settings
          </h2>
          <p className="text-sm text-gray-600 mb-4">
            Put this product on sale with a discounted price.
          </p>
          
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <Checkbox
                id="is_on_sale"
                checked={product.is_on_sale}
                onCheckedChange={(v) => handleChange('is_on_sale', v)}
              />
              <Label htmlFor="is_on_sale" className="cursor-pointer">
                <span className="font-medium">Put on Sale</span>
                <span className="text-sm text-gray-500 block">Enable sale pricing for this product</span>
              </Label>
            </div>
            
            {product.is_on_sale && (
              <div className="mt-4 p-4 bg-white rounded-lg border border-red-200 space-y-4">
                <div>
                  <Label htmlFor="sale_type" className="font-medium">Sale Type *</Label>
                  <Select 
                    value={product.sale_type || ''} 
                    onValueChange={(v) => handleChange('sale_type', v)}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select sale type..." />
                    </SelectTrigger>
                    <SelectContent>
                      {saleTypeOptions.map(option => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {product.sale_type === 'custom' && (
                  <div>
                    <Label htmlFor="sale_type_custom" className="font-medium">Custom Sale Name *</Label>
                    <Input
                      id="sale_type_custom"
                      type="text"
                      value={product.sale_type_custom || ''}
                      onChange={(e) => handleChange('sale_type_custom', e.target.value)}
                      placeholder="Enter custom sale name..."
                      className="mt-1"
                    />
                  </div>
                )}
                
                <div>
                  <Label htmlFor="sale_price" className="font-medium">Sale Price *</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-gray-500">$</span>
                    <Input
                      id="sale_price"
                      type="number"
                      step="0.01"
                      min="0"
                      value={product.sale_price}
                      onChange={(e) => handleChange('sale_price', e.target.value)}
                      placeholder="0.00"
                      className="w-40"
                    />
                  </div>
                </div>
                
                {product.sale_price && product.price && (
                  <div className="p-3 bg-gray-50 rounded text-sm">
                    <div className="flex justify-between">
                      <span>Original Price:</span>
                      <span className="line-through text-gray-400">${parseFloat(product.price).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Sale Price:</span>
                      <span className="font-semibold text-red-600">${parseFloat(product.sale_price).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between border-t pt-2 mt-2">
                      <span>Discount:</span>
                      <span className="font-semibold text-red-600">
                        {Math.round((1 - parseFloat(product.sale_price) / parseFloat(product.price)) * 100)}% off
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Stock Lot Settings */}
        <div className="border rounded-lg p-6 border-orange-200 bg-orange-50/30">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Tag className="w-5 h-5 text-orange-500" />
            Stock Lot Settings
          </h2>
          <p className="text-sm text-gray-600 mb-4">
            Enable this for discontinued products that must be sold as a complete lot (all remaining stock at once).
          </p>
          
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <Checkbox
                id="is_discontinued"
                checked={product.is_discontinued}
                onCheckedChange={(v) => handleChange('is_discontinued', v)}
              />
              <Label htmlFor="is_discontinued" className="cursor-pointer">
                <span className="font-medium">Discontinued Product</span>
                <span className="text-sm text-gray-500 block">Mark this product as no longer available for regular orders</span>
              </Label>
            </div>
            
            <div className="flex items-center space-x-3">
              <Checkbox
                id="is_stock_lot"
                checked={product.is_stock_lot}
                onCheckedChange={(v) => handleChange('is_stock_lot', v)}
                disabled={!product.is_discontinued}
              />
              <Label htmlFor="is_stock_lot" className={`cursor-pointer ${!product.is_discontinued ? 'opacity-50' : ''}`}>
                <span className="font-medium">Sell as Stock Lot</span>
                <span className="text-sm text-gray-500 block">Customers must purchase entire remaining inventory at once</span>
              </Label>
            </div>
            
            {product.is_stock_lot && (
              <div className="mt-4 p-4 bg-white rounded-lg border border-orange-200">
                <Label htmlFor="stock_lot_price" className="font-medium">
                  Stock Lot Price *
                </Label>
                <p className="text-sm text-gray-500 mb-2">
                  Set a discounted price for the entire stock lot
                </p>
                <div className="flex items-center gap-2">
                  <span className="text-gray-500">$</span>
                  <Input
                    id="stock_lot_price"
                    type="number"
                    step="0.01"
                    min="0"
                    value={product.stock_lot_price}
                    onChange={(e) => handleChange('stock_lot_price', e.target.value)}
                    placeholder="0.00"
                    className="w-40"
                  />
                </div>
                {product.inventory && product.inventory.length > 0 && (
                  <div className="mt-3 p-3 bg-gray-50 rounded text-sm">
                    <p className="font-medium text-gray-700">
                      Total Stock: {product.inventory.reduce((acc, inv) => acc + (inv.quantity || 0), 0)} units
                    </p>
                    <p className="text-gray-500">
                      Original Value: ${(product.inventory.reduce((acc, inv) => acc + (inv.quantity || 0), 0) * (parseFloat(product.price) || 0)).toFixed(2)}
                    </p>
                    {product.stock_lot_price && (
                      <p className="text-green-600 font-medium">
                        Discount: {Math.round((1 - parseFloat(product.stock_lot_price) / (product.inventory.reduce((acc, inv) => acc + (inv.quantity || 0), 0) * (parseFloat(product.price) || 1))) * 100)}% off
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Coming Soon Settings */}
        <div className="border rounded-lg p-6 border-blue-200 bg-blue-50/30">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-blue-500" />
            New Product Coming Soon
          </h2>
          <p className="text-sm text-gray-600 mb-4">
            Mark this product as "Coming Soon" for new arrivals that haven't landed yet but you want to showcase.
          </p>
          
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <Checkbox
                id="is_coming_soon"
                checked={product.is_coming_soon}
                onCheckedChange={(v) => handleChange('is_coming_soon', v)}
              />
              <Label htmlFor="is_coming_soon" className="cursor-pointer">
                <span className="font-medium">New Product Coming Soon</span>
                <span className="text-sm text-gray-500 block">Product is not yet available but will be arriving soon</span>
              </Label>
            </div>
            
            {product.is_coming_soon && (
              <div className="mt-4 p-4 bg-white rounded-lg border border-blue-200 space-y-4">
                <div>
                  <Label htmlFor="expected_date" className="font-medium">Due in Store Date</Label>
                  <Input
                    id="expected_date"
                    type="date"
                    value={product.expected_date || ''}
                    onChange={(e) => handleChange('expected_date', e.target.value)}
                    className="mt-1 w-48"
                  />
                </div>
                <div className="p-3 bg-blue-50 rounded text-sm text-blue-700">
                  <p>This product will be displayed with a "NEW ARRIVAL COMING SOON" badge. Customers can still place orders and you'll process them when stock arrives.</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* NEW Product Badge */}
        <div className="border rounded-lg p-6 border-green-200 bg-green-50/30">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-green-500" />
            New Product Badge
          </h2>
          <p className="text-sm text-gray-600 mb-4">
            Highlight this product as new to attract customer attention.
          </p>
          
          <div className="flex items-center space-x-3">
            <Checkbox
              id="is_new"
              checked={product.is_new}
              onCheckedChange={(v) => handleChange('is_new', v)}
            />
            <Label htmlFor="is_new" className="cursor-pointer">
              <span className="font-medium">Mark as NEW</span>
              <span className="text-sm text-gray-500 block">Display a "NEW" badge on this product</span>
            </Label>
          </div>
        </div>

        {/* Inventory */}
        <div className="border rounded-lg p-6">
          <h2 className="text-lg font-semibold mb-4">Inventory, Stock & Barcodes</h2>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left p-3 font-semibold">Size</th>
                  <th className="text-left p-3 font-semibold">SKU</th>
                  <th className="text-left p-3 font-semibold">Barcode</th>
                  <th className="text-left p-3 font-semibold">Stock Qty</th>
                  <th className="text-left p-3 font-semibold">Restock ETA</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {sizes.map(size => {
                  // Generate SKU with size
                  const baseCategory = product.category?.toLowerCase() || '';
                  const sizingType = product.sizing_type || 'unisex';
                  let skuWithSize = product.sku || '';
                  
                  if (skuWithSize) {
                    if (['caps', 'bags'].includes(baseCategory)) {
                      // No suffix for caps/bags
                    } else if (sizingType === 'kids') {
                      const kidsMap = {'1-2': 'XXS', '3-4': 'XS', '5-6': 'S', '7-8': 'M', '9-10': 'L', '11-12': 'XL'};
                      skuWithSize = `${product.sku}${kidsMap[size] || size}`;
                    } else if (size !== 'OS' && size !== 'One Size') {
                      skuWithSize = `${product.sku}${size.replace('/', '')}`;
                    }
                  }
                  
                  return (
                    <tr key={size}>
                      <td className="p-3">
                        <span className="font-medium">{size === 'OS' ? 'One Size' : size}</span>
                      </td>
                      <td className="p-3">
                        <span className="font-mono text-sm text-gray-600">{skuWithSize}</span>
                      </td>
                      <td className="p-3">
                        <Input
                          type="text"
                          value={getInventoryValue(size, 'barcode') || ''}
                          onChange={(e) => handleInventoryChange(size, 'barcode', e.target.value)}
                          placeholder="Enter barcode"
                          className="w-36 font-mono text-sm"
                        />
                      </td>
                      <td className="p-3">
                        <Input
                          type="number"
                          min="0"
                          value={getInventoryValue(size, 'quantity')}
                          onChange={(e) => handleInventoryChange(size, 'quantity', e.target.value)}
                          className="w-24"
                        />
                      </td>
                      <td className="p-3">
                        <Input
                          type="date"
                          value={getInventoryValue(size, 'eta') || ''}
                          onChange={(e) => handleInventoryChange(size, 'eta', e.target.value)}
                          className="w-36"
                          disabled={getInventoryValue(size, 'quantity') > 0}
                        />
                        {getInventoryValue(size, 'quantity') > 0 && (
                          <span className="text-xs text-gray-400 ml-2">In stock</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pb-8">
          <Button variant="outline" onClick={() => navigate('/admin/products')}>
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="bg-[#002FA7]"
          >
            {saving ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
            ) : (
              <><Save className="w-4 h-4 mr-2" /> {isNew ? 'Create Product' : 'Save Changes'}</>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminProductEdit;
